// sensorPackService.js
import config from '../config';

// Export metrics as CSV
export const exportMetricsAsCSV = async (token, params) => {
  try {
    const {
      sensorPackId,
      rawSensors = [],
      calibratedSensors = [],
      startDate,
      endDate
    } = params;

    // Manually build the query string to avoid URL encoding of commas
    let queryParts = [];

    // Add comma-separated raw sensors
    if (rawSensors.length > 0) {
      queryParts.push(`fields_standard=${rawSensors.join(',')}`);
    }

    // Add comma-separated calibrated sensors
    if (calibratedSensors.length > 0) {
      queryParts.push(`fields_calibrated=${calibratedSensors.join(',')}`);
    }

    // Add other parameters
    if (startDate) {
      queryParts.push(`start_date=${startDate}`);
    }

    if (endDate) {
      queryParts.push(`end_date=${endDate}`);
    }

    queryParts.push(`sensor_pack=${sensorPackId}`);

    // Construct the URL with the query string
    const url = `${config.API_URL}/export-metrics/?${queryParts.join('&')}`;

    console.log('Requesting CSV export URL:', url); // For debugging

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Authorization": `Token ${token}`,
        "Accept": "*/*", // Accept any content type returned by the server
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API Error (${response.status}):`, errorText);
      throw new Error(`Failed to export metrics: ${response.status}`);
    }

    return await response.blob();
  } catch (error) {
    console.error("Error exporting metrics:", error);
    throw error;
  }
};

// Fetch a specific sensor pack by ID
export const getSensorPackById = async (id, token) => {
  try {
    const response = await fetch(`${config.API_URL}/sensor-packs/${id}/`, {
      method: "GET",
      headers: {
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch sensor pack: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching sensor pack:", error);
    throw error;
  }
};

// Fetch all sensor packs
export const getAllSensorPacks = async (token) => {
  try {
    const response = await fetch(`${config.API_URL}/sensor-packs/`, {
      method: "GET",
      headers: {
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch sensor packs: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching sensor packs:", error);
    throw error;
  }
};

// Fetch paginated metric standards for a sensor pack (for the table view)
export const getMetricStandards = async (sensorPackId, token, page = 1, pageSize = 10) => {
  try {
    const response = await fetch(`${config.API_URL}/metrics/standards/?sensor_pack=${sensorPackId}&page=${page}&page_size=${pageSize}`, {
      method: "GET",
      headers: {
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch metric standards: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching metric standards:", error);
    throw error;
  }
};

// Fetch filtered metrics based on sensor selections and date range
export const getFilteredMetrics = async (token, params) => {
  try {
    const {
      sensorPackId,
      rawSensors = [],
      calibratedSensors = [],
      startDate,
      endDate,
      format
    } = params;

    // Manually build the query string to avoid URL encoding of commas
    let queryParts = [];

    // Add comma-separated raw sensors
    if (rawSensors.length > 0) {
      queryParts.push(`fields_standard=${rawSensors.join(',')}`);
    }

    // Add comma-separated calibrated sensors
    if (calibratedSensors.length > 0) {
      queryParts.push(`fields_calibrated=${calibratedSensors.join(',')}`);
    }

    // Add other parameters
    if (startDate) {
      queryParts.push(`start_date=${startDate}`);
    }

    if (endDate) {
      queryParts.push(`end_date=${endDate}`);
    }

    queryParts.push(`sensor_pack=${sensorPackId}`);

    if (format) {
      queryParts.push(`format=${format}`);
    }

    // Construct the URL with the query string
    const url = `${config.API_URL}/metrics/?${queryParts.join('&')}`;

    console.log('Requesting URL:', url); // For debugging

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API Error (${response.status}):`, errorText);
      throw new Error(`Failed to fetch filtered metrics: ${response.status}`);
    }

    // If format is CSV, return the blob for download
    if (format === 'csv') {
      return await response.blob();
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching filtered metrics:", error);
    throw error;
  }
};